<template>
  <div>
    <b-col md="12">
      <div>
        <b-card>
          <div>
            <!-- Print Button and Selection Actions -->
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h2>{{ $t("accounting.refund-invoices") }}</h2>
              <div class="d-flex gap-2">
                <b-button
                  v-if="canCreateRefundInvoices"
                  variant="outline-danger"
                  size="sm"
                  :disabled="creatingRefund"
                  @click="openCreateRefundModal"
                >
                  <feather-icon
                    icon="RotateCcwIcon"
                    size="14"
                    class="mr-1"
                  />
                  {{ $t("reception.create-refund-invoice") }}
                </b-button>
                <b-button
                  v-if="!allSelected"
                  variant="outline-primary"
                  size="sm"
                  @click="selectAll"
                >
                  <feather-icon
                    icon="CheckSquareIcon"
                    size="14"
                    class="mr-1"
                  />
                  {{ $t("common.select-all") }}
                </b-button>
                <b-button
                  v-if="selectedInvoices.length > 0"
                  variant="warning"
                  size="sm"
                  @click="showBulkUpdateModal"
                >
                  <feather-icon
                    icon="EditIcon"
                    size="14"
                    class="mr-1"
                  />
                  {{ $t("common.bulk-update-status") }} ({{ selectedInvoices.length }})
                </b-button>
                <b-button
                  v-if="selectedInvoices.length > 0"
                  variant="outline-secondary"
                  size="sm"
                  @click="clearSelection"
                >
                  {{ $t("common.clear-selection") }}
                </b-button>
              </div>
            </div>

            <!-- Data Table -->
            <b-table
              v-if="!loading"
              :items="this.invoices.data"
              responsive
              :fields="tableColumns"
              primary-key="id"
              show-empty
              empty-text="No matching results found"
              class="position-relative"
            >
              <!-- Checkbox for selection -->
              <template #cell(select)="data">
                <b-form-checkbox
                  v-model="selectedInvoices"
                  :value="data.item"
                  @change="onSelectionChange"
                />
              </template>

              <template #cell(invoice_no)="data">
                <span class="font-weight-bold text-primary">
                  #{{ data.item.invoice_no }}
                </span>
              </template>

              <template #cell(service_items)="data">
                <div
                  v-if="data.item.description"
                  class="refund-description"
                >
                  <div class="d-flex align-items-start">
                    <!-- <feather-icon icon="InfoIcon" size="16" class="text-warning mr-2 " /> -->
                    <div>
                      <span
                        class="font-weight-bold text-dark text-nowrap"
                        style="font-size: 0.9rem;"
                      >
                        {{ data.item.description }}
                      </span>
                    </div>
                  </div>
                </div>
                <span
                  v-else
                  class="text-muted"
                >{{ $t('accounting.refund-invoice') }}</span>
              </template>

              <template #cell(discount)="data">
                <span class="text-nowrap font-weight-bold text-warning">
                  {{ formatPrice(data.item.discount || 0) }}
                </span>
              </template>

              <template #cell(amount)="data">
                <span class="text-nowrap font-weight-bolder text-danger">
                  - {{ formatPrice(data.item.amount) }}
                </span>
              </template>

              <template #cell(status)="data">
                <div>
                  <ChangeInvoiceStatusDropDown
                    name="refundInvoice"
                    endpoint="invoices"
                    :data="data.item"
                    @onEmit="$emit('onChangeStatus')"
                  />
                </div>
              </template>

              <!-- Column: Actions -->
              <template #cell(actions)="data">
                <div class="text-nowrap d-flex">
                  <b-button
                    v-if="data.item.status == 'approved' "
                    variant="outline-primary"
                    size="sm"
                    class="mx-1"
                    @click="showRefundInvoice(data.item)"
                  >
                    {{ $t("reception.Print Invoice") }}
                  </b-button>
                  <b-button
                    v-if="$store.state.app.permissions.includes('apply-invoice-discount') && data.item.status !== 'printed' && data.item.status !== 'approved'"
                    variant="outline-warning"
                    size="sm"
                    class="mx-1"
                    @click="showDiscountModal(data.item)"
                  >
                    <feather-icon
                      icon="PercentIcon"
                      size="14"
                      class="mr-1"
                    />
                    {{ $t("common.apply-discount") }}
                  </b-button>
                </div>
              </template>
            </b-table>
            <template v-if="loading">
              <div>
                <div class="d-flex justify-content-center mb-1">
                  <b-spinner label="Loading..." />
                </div>
              </div>
            </template>
            <div class="mx-2 mb-2">
              <b-row>
                <b-col
                  cols="12"
                  sm="6"
                  class="d-flex align-items-center justify-content-center justify-content-sm-start"
                >
                  <span
                    class="text-muted"
                  >{{ $t("common.showing") }} 1 {{ $t("common.to") }} 10
                  </span>
                </b-col>
                <!-- Pagination -->
                <b-col
                  cols="12"
                  sm="6"
                  class="d-flex align-items-center justify-content-center justify-content-sm-end"
                  @click="getInvoices()"
                >
                  <b-pagination
                    v-model="page"
                    :total-rows="dataCount"
                    :per-page="take"
                    first-number
                    last-number
                    class="mb-0 mt-1 mt-sm-0"
                    prev-class="prev-item"
                    next-class="next-item"
                  >
                    <template #prev-text>
                      <feather-icon
                        icon="ChevronLeftIcon"
                        size="18"
                      />
                    </template>
                  </b-pagination>
                </b-col>
              </b-row>
            </div>
          </div>
        </b-card>
      </div>

      <!-- Bulk Update Status Modal -->
      <b-modal
        id="modal-bulk-update-status-refund"
        :no-close-on-backdrop="true"
        :title="$t('common.bulk-update-status')"
        ok-only
        :ok-title="$t('common.update')"
        @ok="bulkUpdateStatus"
      >
        <b-card-text>
          <h5>{{ $t('common.bulk-update-status') }}</h5>
          <p>{{ $t('common.selected-invoices-count', { count: selectedInvoices.length }) }}</p>

          <b-form-group
            :label="$t('common.status')"
            label-for="bulk-status"
          >
            <b-form-select
              id="bulk-status"
              v-model="bulkUpdateForm.status"
              :options="statusOptions"
              :placeholder="$t('common.select-status')"
            />
          </b-form-group>

          <div class="mt-3">
            <h6>{{ $t('common.selected-invoices') }}:</h6>
            <ul class="list-unstyled">
              <li
                v-for="invoice in selectedInvoices"
                :key="invoice.id"
                class="mb-1"
              >
                <small class="text-muted">
                  #{{ getNestedProperty(invoice, 'invoice_no') }} -
                  - {{ formatPrice(getNestedProperty(invoice, 'amount')) }}
                </small>
              </li>
            </ul>
          </div>
        </b-card-text>
      </b-modal>

      <!-- Create Refund Invoice Modal -->
      <b-modal
        id="modal-create-refund-invoice-from-list"
        centered
        size="lg"
        :title="$t('reception.create-refund-invoice')"
        :ok-disabled="creatingRefund || refundSummaryLoading || !isRefundFormEnabled"
        @ok="submitCreateRefundInvoice"
      >
        <b-card-text>
          <div
            v-if="refundCandidatesLoading"
            class="d-flex align-items-center"
          >
            <b-spinner
              small
              class="mr-1"
            />
            <span>{{ $t("common.loading") }}</span>
          </div>

          <template v-else>
            <b-form-group
              :label="$t('common.search')"
              label-for="refund-invoice-search-input"
            >
              <b-form-input
                id="refund-invoice-search-input"
                v-model.trim="refundInvoiceSearch"
                :placeholder="$t('hospital.invoice-no')"
              />
            </b-form-group>

            <div class="border rounded p-1 mb-2 refund-candidates-box">
              <div
                v-if="filteredRefundCandidates.length === 0"
                class="text-muted"
              >
                {{ $t('common.no-data') }}
              </div>
              <div
                v-for="candidate in filteredRefundCandidates"
                :key="candidate.id"
                class="d-flex justify-content-between align-items-center py-50 border-bottom"
              >
                <div>
                  <strong>#{{ candidate.invoice_no }}</strong>
                  <span class="mx-1">-</span>
                  <span>{{ formatPrice(candidate.amount) }}</span>
                  <span class="mx-1">-</span>
                  <span class="text-muted">{{ getInvoiceTypeLabelAr(candidate.type) }}</span>
                </div>
                <b-button
                  size="sm"
                  variant="outline-primary"
                  :disabled="refundSummaryLoading"
                  @click="selectRefundCandidate(candidate)"
                >
                  <b-spinner
                    v-if="refundSummaryLoading && refundForm.original_invoice_id === candidate.id"
                    small
                    class="mr-50"
                  />
                  {{ $t('common.select') }}
                </b-button>
              </div>
            </div>

            <b-form-group
              :label="$t('reception.select-invoice-before-refund')"
              label-for="refund-source-invoice"
            >
              <b-form-select
                id="refund-source-invoice"
                v-model="refundForm.original_invoice_id"
                :options="refundInvoiceOptions"
                :state="refundErrors.original_invoice_id ? false : null"
              />
              <small
                v-if="refundErrors.original_invoice_id"
                class="text-danger d-block mt-50"
              >
                {{ refundErrors.original_invoice_id }}
              </small>
            </b-form-group>

            <b-alert
              v-if="refundSummaryError"
              variant="warning"
              show
              class="mb-2"
            >
              {{ refundSummaryError }}
            </b-alert>

            <div
              v-if="refundSummaryData"
              class="border rounded p-1 mb-2"
            >
              <div class="d-flex justify-content-between">
                <span>{{ $t('hospital.invoice-no') }}</span>
                <strong>#{{ refundSummaryData.invoice_no }}</strong>
              </div>
              <div class="d-flex justify-content-between">
                <span>{{ $t('common.type') }}</span>
                <strong>{{ getInvoiceTypeLabelAr(refundSummaryData.invoice_type) }}</strong>
              </div>
              <div class="d-flex justify-content-between">
                <span>{{ $t('common.status') }}</span>
                <strong>{{ refundSummaryData.invoice_status }}</strong>
              </div>
              <div class="d-flex justify-content-between">
                <span>{{ $t('common.amount') }}</span>
                <strong>{{ formatPrice(refundSummaryData.original_amount) }}</strong>
              </div>
              <div class="d-flex justify-content-between">
                <span>{{ $t('common.discount') }}</span>
                <strong>{{ formatPrice(refundSummaryData.discount || 0) }}</strong>
              </div>
              <div class="d-flex justify-content-between">
                <span>{{ $t('accounting.total') }}</span>
                <strong>{{ formatPrice(refundSummaryData.net_amount) }}</strong>
              </div>
              <div class="d-flex justify-content-between">
                <span>{{ $t('reception.refund-total-refunded') }}</span>
                <strong>{{ formatPrice(refundSummaryData.total_refunded || 0) }}</strong>
              </div>
              <div class="d-flex justify-content-between">
                <span>{{ $t('reception.refund-remaining-amount') }}</span>
                <strong>{{ formatPrice(refundSummaryData.refundable_amount || 0) }}</strong>
              </div>
              <div class="d-flex justify-content-between">
                <span>{{ $t('common.status') }}</span>
                <b-badge :variant="isRefundFormEnabled ? 'light-success' : 'light-danger'">
                  {{ isRefundFormEnabled ? 'Can be refunded' : 'Cannot be refunded' }}
                </b-badge>
              </div>

              <div
                v-if="refundSummaryData.refunds && refundSummaryData.refunds.length"
                class="mt-1"
              >
                <h6 class="mb-50">
                  {{ $t('reception.refund-previous-refunds') }}
                </h6>
                <div
                  v-for="refund in refundSummaryData.refunds"
                  :key="refund.id"
                  class="d-flex justify-content-between border-top pt-50 pb-50"
                >
                  <div>
                    <strong>#{{ refund.invoice_no }}</strong>
                    <span class="mx-50">-</span>
                    <span>{{ refund.status }}</span>
                    <div class="text-muted small">
                      {{ refund.description }}
                    </div>
                  </div>
                  <div class="text-right">
                    <div>{{ formatPrice(refund.amount) }}</div>
                    <div class="text-muted small">
                      {{ refund.date }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <b-form-group
              :label="$t('accounting.refund-amount')"
              label-for="refund-create-amount-input"
            >
              <b-form-input
                id="refund-create-amount-input"
                v-model.number="refundForm.amount"
                type="number"
                min="0"
                :placeholder="$t('accounting.refund-amount')"
                :state="refundErrors.amount ? false : null"
                :disabled="!isRefundFormEnabled || refundSummaryLoading"
              />
              <small
                v-if="refundErrors.amount"
                class="text-danger d-block mt-50"
              >
                {{ refundErrors.amount }}
              </small>
            </b-form-group>

            <b-form-group
              :label="$t('accounting.refund-reason')"
              label-for="refund-create-reason-input"
            >
              <b-form-input
                id="refund-create-reason-input"
                v-model="refundForm.reason"
                :placeholder="$t('accounting.refund-reason')"
                :state="refundErrors.reason ? false : null"
                :disabled="!isRefundFormEnabled || refundSummaryLoading"
              />
              <small
                v-if="refundErrors.reason"
                class="text-danger d-block mt-50"
              >
                {{ refundErrors.reason }}
              </small>
            </b-form-group>

            <b-form-group
              :label="$t('common.description')"
              label-for="refund-create-description-input"
            >
              <b-form-input
                id="refund-create-description-input"
                v-model="refundForm.description"
                :placeholder="$t('reception.refund-description-placeholder')"
                :state="refundErrors.description ? false : null"
                :disabled="!isRefundFormEnabled || refundSummaryLoading"
              />
              <small
                v-if="refundErrors.description"
                class="text-danger d-block mt-50"
              >
                {{ refundErrors.description }}
              </small>
            </b-form-group>

            <b-form-group
              :label="$t('common.date')"
              label-for="refund-create-date-input"
            >
              <b-form-input
                id="refund-create-date-input"
                v-model="refundForm.date"
                type="date"
                :state="refundErrors.date ? false : null"
                :disabled="!isRefundFormEnabled || refundSummaryLoading"
              />
              <small
                v-if="refundErrors.date"
                class="text-danger d-block mt-50"
              >
                {{ refundErrors.date }}
              </small>
            </b-form-group>
          </template>
        </b-card-text>
      </b-modal>

      <!-- Modal to apply discount -->
      <b-modal
        id="modal-apply-discount-refund"
        centered
        size="md"
        :title="$t('common.apply-discount')"
        @ok="applyDiscount"
      >
        <b-card-text>
          <b-form-group
            :label="$t('common.invoice-number')"
            label-for="discount-invoice-no"
          >
            <b-form-input
              id="discount-invoice-no"
              :value="'#' + (discountForm.invoice_no || '')"
              disabled
            />
          </b-form-group>

          <b-form-group
            :label="$t('common.current-amount')"
            label-for="discount-current-amount"
          >
            <b-form-input
              id="discount-current-amount"
              :value="formatPrice(discountForm.amount)"
              disabled
            />
          </b-form-group>

          <b-form-group
            :label="$t('common.discount-amount')"
            label-for="discount-amount"
            :invalid-feedback="discountError"
            :state="discountError ? false : null"
          >
            <b-form-input
              id="discount-amount"
              v-model="discountForm.discount"
              type="number"
              min="0"
              :placeholder="$t('common.enter-discount-amount')"
              :state="discountError ? false : null"
            />
          </b-form-group>

          <b-alert
            v-if="discountForm.discount > 0"
            variant="info"
            show
            class="mt-2"
          >
            <div class="d-flex justify-content-between">
              <span>{{ $t('common.amount-after-discount') }}:</span>
              <strong>- {{ formatPrice(calculateAmountAfterDiscount()) }}</strong>
            </div>
          </b-alert>
        </b-card-text>
      </b-modal>

      <!-- Print Confirmation Modal -->
      <b-modal
        id="modal-confirm-print-refund"
        :title="$t('surgery.print-invoice')"
        ok-only
        :ok-title="$t('common.print')"
        @ok="printInvoice"
      >
        <b-card-text>
          <h5>{{ $t("surgery.print-invoice") }}</h5>
          {{ $t("surgery.print-sure") }}
        </b-card-text>
      </b-modal>

      <div ref="printContent">
        <RefundInvoice
          :patient-data="patientData"
          :data="invoice"
        />
      </div>
    </b-col>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BLink,
  BDropdown,
  BSpinner,
  BDropdownItem,
  BTooltip,
  BFormGroup,
  BFormSelect,
  BModal,
  BCardText,
  BFormCheckboxGroup,
  BFormCheckbox,
  BCardBody,
  BPagination,
  BTable,
  BBadge,
  BAlert,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import VuePhoneNumberInput from 'vue-phone-number-input'
import 'vue-phone-number-input/dist/vue-phone-number-input.css'
import { getNestedProperty } from '@/utils/nestedProperty.js'

import ActionsButtons from '@/components/common/ActionsButtons.vue'
import DeleteModal from '@/components/modals/DeleteModal.vue'

const AutoComplete = () => import('@/components/common/AutoComplete.vue')
const RefundInvoice = () => import('@/components/printables/RefundInvoice.vue')
const ChangeInvoiceStatusDropDown = () => import('@/components/invoices/ChangeInvoiceStatusDropDown.vue')

export default {
  components: {
    BCard,
    BRow,
    BBadge,
    BCol,
    BFormInput,
    BButton,
    BLink,
    BDropdown,
    BDropdownItem,
    BTooltip,
    vSelect,
    ToastificationContent,
    VuePhoneNumberInput,
    DeleteModal,
    ActionsButtons,
    BFormGroup,
    BFormSelect,
    BSpinner,
    BModal,
    BCardText,
    BFormCheckbox,
    BFormCheckboxGroup,
    BCardBody,
    BPagination,
    BTable,
    BAlert,
    ChangeInvoiceStatusDropDown,
    RefundInvoice,
  },
  props: ['invoices', 'patientData'],
  data() {
    return {
      tableColumns: [
        {
          key: 'select',
          label: '',
          thStyle: { width: '50px' },
        },
        {
          key: 'invoice_no',
          sortable: true,
          label: this.$t('hospital.invoice-no'),
        },
        {
          key: 'service_items',
          label: this.$t('accounting.refund-reason'),
          sortable: false,
        },
        {
          key: 'discount',
          label: this.$t('common.discount'),
          sortable: true,
        },
        {
          key: 'amount',
          label: this.$t('employee.amount'),
          sortable: true,
        },
        {
          key: 'created_at',
          sortable: true,
          label: this.$t('common.created-at'),
        },
        {
          key: 'status',
          sortable: true,
          label: 'Status',
        },
        { key: 'Actions', label: this.$t('common.actions') },
      ],
      invoiceLatestStatus: '',
      InvoiceId: '',
      invoice: '',
      currentInvoice: null,
      selectedInvoices: [],
      discountForm: {
        id: null,
        invoice_no: '',
        amount: 0,
        discount: 0,
      },
      discountError: '',
      creatingRefund: false,
      refundCandidatesLoading: false,
      refundCandidates: [],
      refundInvoiceSearch: '',
      refundForm: {
        original_invoice_id: null,
        amount: null,
        reason: '',
        description: '',
        date: new Date().toISOString().slice(0, 10),
      },
      refundErrors: {},
      refundSummaryLoading: false,
      refundSummaryData: null,
      refundSummaryError: null,
      bulkUpdateForm: {
        status: null,
      },
      statusOptions: [
        { value: 'approved', text: 'Approved' },
        { value: 'rejected', text: 'Rejected' },
      ],
      currentPage: 1,
      takeOptions: [10, 25, 50, 100],
      searchQuery: '',
      sortBy: 'id',
      isSortDirDesc: true,
      page: 1,
      dataCount: 0,
      loading: false,
    }
  },

  computed: {
    allSelectedInvoicesApproved() {
      return this.selectedInvoices.length > 0
             && this.selectedInvoices.every(invoice => invoice.status === 'approved')
    },
    canCreateRefundInvoices() {
      return this.$store.state.app.permissions.includes('update-invoice')
    },
    refundInvoiceOptions() {
      const base = [
        {
          value: null,
          text: this.$t('reception.select-invoice-before-refund'),
        },
      ]
      const dynamicOptions = this.refundCandidates.map(invoice => ({
        value: invoice.id,
        text: `#${invoice.invoice_no} - ${this.formatPrice(invoice.amount)} - ${this.getInvoiceTypeLabelAr(invoice.type)}`,
      }))
      return [...base, ...dynamicOptions]
    },
    filteredRefundCandidates() {
      if (!this.refundInvoiceSearch) return this.refundCandidates
      const keyword = String(this.refundInvoiceSearch).toLowerCase()
      return this.refundCandidates.filter(invoice => {
        const invoiceNo = String(invoice.invoice_no || '').toLowerCase()
        return invoiceNo.includes(keyword)
      })
    },
    isRefundFormEnabled() {
      return (
        !this.refundSummaryLoading
        && !!this.refundSummaryData
        && this.refundSummaryData.can_be_refunded === true
      )
    },

    allSelected: {
      get() {
        const invoicesData = getNestedProperty(this.invoices, 'data', [])
        return invoicesData.length > 0 && this.selectedInvoices.length === invoicesData.length
      },
      set(value) {
        // Handled by toggleSelectAll
      },
    },
  },
  watch: {
    'refundForm.original_invoice_id': function (invoiceId) {
      const selectedInvoice = this.refundCandidates.find(invoice => invoice.id === invoiceId)
      if (selectedInvoice) {
        this.refundForm.amount = Number(selectedInvoice.amount) || null
      }
    },
  },

  mounted() {},
  activated() {
    // Refresh data when component becomes active
    this.$emit('onChangeStatus')
  },
  methods: {
    getNestedProperty,

    // Format price with currency
    formatPrice(price) {
      return price ? `${Number(price).toLocaleString()} IQD` : '0 IQD'
    },
    getPatientCardId() {
      return getNestedProperty(this.patientData, 'id')
        || getNestedProperty(this.patientData, 'patient_card_id')
        || getNestedProperty(this.patientData, 'patientCard.id')
        || null
    },
    getCurrentPatientId() {
      return getNestedProperty(this.$route, 'params.patientId')
        || getNestedProperty(this.patientData, 'patient.id')
        || getNestedProperty(this.patientData, 'patient_id')
        || null
    },
    getInvoiceTypeLabelAr(type) {
      const normalizedType = String(type || '').toLowerCase()
      const typeMap = {
        invoice_out_clinic: 'cashiers.invoice-type-out-clinic',
        invoice_nicu: 'cashiers.invoice-type-nicu',
        invoice_patient_card_test: 'cashiers.invoice-type-patient-card-test',
        invoice_emergency: 'cashiers.invoice-type-emergency',
        invoice_insurance_surgery: 'cashiers.invoice-type-insurance-surgery',
        refund: 'cashiers.invoice-type-refund',
      }
      const key = typeMap[normalizedType]
      if (!key) return type || '-'
      const translated = this.$t(key)
      return translated && translated !== key ? translated : (type || '-')
    },
    isInvoiceRefundable(invoice) {
      if (!invoice) return false
      const status = String(invoice.status || '').toLowerCase()
      const type = String(invoice.type || '').toLowerCase()
      return type !== 'refund' && !['pending', 'refund', 'rejected'].includes(status)
    },
    resetCreateRefundForm() {
      this.refundForm = {
        original_invoice_id: null,
        amount: null,
        reason: '',
        description: '',
        date: new Date().toISOString().slice(0, 10),
      }
      this.refundErrors = {}
    },
    async openCreateRefundModal() {
      this.resetCreateRefundForm()
      this.refundCandidates = []
      this.refundInvoiceSearch = ''
      this.$bvModal.show('modal-create-refund-invoice-from-list')
      await this.loadRefundableInvoices()
    },
    async loadRefundableInvoices() {
      this.refundCandidatesLoading = true
      try {
        const patientId = this.getCurrentPatientId()
        if (!patientId) {
          this.refundCandidates = []
          return
        }
        const query = `?filter[patientCard.patient_id]=${patientId}&take=2000&sort=-id`
        const response = await this.$http.get(`/invoices${query}`)
        const rawInvoices = getNestedProperty(response, 'data.data', [])
        this.refundCandidates = rawInvoices.filter(item => this.isInvoiceRefundable(item))
      } catch (error) {
        console.error('Error loading refundable invoices:', error)
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.error'),
            variant: 'danger',
            text: this.$t('common.error-occurred'),
          },
        })
      } finally {
        this.refundCandidatesLoading = false
      }
    },
    validateCreateRefundForm() {
      const errors = {}
      const selectedInvoice = this.refundCandidates.find(
        invoice => invoice.id === this.refundForm.original_invoice_id,
      )
      const summaryAmount = Number(getNestedProperty(this.refundSummaryData, 'refundable_amount', 0))
      const invoiceAmount = Number(getNestedProperty(selectedInvoice, 'amount', 0)) || 0
      const maxAmount = Number.isNaN(summaryAmount) || summaryAmount <= 0
        ? invoiceAmount
        : summaryAmount

      if (!this.refundForm.original_invoice_id) {
        errors.original_invoice_id = this.$t('reception.select-invoice-before-refund')
      }
      if (
        this.refundForm.amount === null
        || this.refundForm.amount === ''
        || Number(this.refundForm.amount) <= 0
      ) {
        errors.amount = this.$t('reception.refund-amount-required')
      } else if (maxAmount > 0 && Number(this.refundForm.amount) > maxAmount) {
        errors.amount = this.$t('reception.refund-amount-exceeds')
      }
      if (!this.refundForm.reason || !this.refundForm.reason.trim()) {
        errors.reason = this.$t('reception.refund-reason-required')
      }
      if (!this.refundForm.date) {
        errors.date = this.$t('reception.refund-date-required')
      }
      if (
        this.refundSummaryError
        || (this.refundSummaryData && this.refundSummaryData.can_be_refunded === false)
      ) {
        errors.amount = errors.amount || this.$t('reception.refund-not-allowed')
      }

      this.refundErrors = errors
      return Object.keys(errors).length === 0
    },
    async loadRefundSummary(invoice) {
      if (!invoice || !invoice.id) return
      this.refundSummaryLoading = true
      this.refundSummaryError = null
      this.refundSummaryData = null
      try {
        const response = await this.$http.get(`/invoices/${invoice.id}/refund-summary`)
        const summary = getNestedProperty(response, 'data.data', null)
        this.refundSummaryData = summary

        if (!summary) {
          this.refundSummaryError = this.$t('reception.refund-summary-failed')
          return
        }

        const summaryStatus = String(summary.invoice_status || invoice.status || '').toLowerCase()
        if (
          ['pending', 'refund', 'rejected'].includes(summaryStatus)
          || summary.can_be_refunded === false
          || Number(summary.refundable_amount) <= 0
        ) {
          this.refundSummaryError = this.$t('reception.refund-not-allowed')
        }

        const refundableAmount = Number(summary.refundable_amount)
        if (!Number.isNaN(refundableAmount) && refundableAmount > 0) {
          this.refundForm.amount = refundableAmount
        } else {
          this.refundForm.amount = Number(invoice.amount) || null
        }
      } catch (error) {
        this.refundSummaryError = error.response?.data?.message || this.$t('reception.refund-summary-failed')
        this.refundForm.amount = Number(invoice.amount) || null
      } finally {
        this.refundSummaryLoading = false
      }
    },
    async selectRefundCandidate(invoice) {
      this.refundForm.original_invoice_id = invoice.id
      this.refundErrors = {}
      await this.loadRefundSummary(invoice)
      if (this.refundSummaryError) {
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.error'),
            variant: 'warning',
            text: this.refundSummaryError,
          },
        })
      }
    },
    async submitCreateRefundInvoice(bvModalEvt) {
      bvModalEvt.preventDefault()
      if (this.creatingRefund) return
      if (!this.validateCreateRefundForm()) return

      this.creatingRefund = true
      try {
        const payload = {
          original_invoice_id: this.refundForm.original_invoice_id,
          amount: Number(this.refundForm.amount),
          reason: this.refundForm.reason.trim(),
          description: this.refundForm.description
            ? this.refundForm.description.trim()
            : null,
          date: this.refundForm.date,
        }
        await this.$http.post('/invoices/refund', payload)

        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.success'),
            variant: 'success',
            text: this.$t('reception.refund-created-successfully'),
          },
        })

        this.$bvModal.hide('modal-create-refund-invoice-from-list')
        this.resetCreateRefundForm()
        this.$emit('onChangeStatus')
      } catch (error) {
        const responseErrors = error.response?.data?.errors || error.response?.data
        if (responseErrors && typeof responseErrors === 'object') {
          const normalizedErrors = { ...this.refundErrors };
          ['original_invoice_id', 'amount', 'reason', 'description', 'date'].forEach(field => {
            if (responseErrors[field]) {
              normalizedErrors[field] = Array.isArray(responseErrors[field])
                ? responseErrors[field][0]
                : responseErrors[field]
            }
          })
          this.refundErrors = normalizedErrors
        }
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.error'),
            variant: 'danger',
            text: error.response?.data?.message || this.$t('common.error-occurred'),
          },
        })
      } finally {
        this.creatingRefund = false
      }
    },

    // Show discount modal
    showDiscountModal(invoice) {
      this.discountForm = {
        id: invoice.id,
        invoice_no: invoice.invoice_no || '',
        amount: parseFloat(invoice.amount) || 0,
        discount: parseFloat(invoice.discount) || 0,
      }
      this.discountError = ''
      this.$bvModal.show('modal-apply-discount-refund')
    },

    // Calculate amount after discount
    calculateAmountAfterDiscount() {
      const amount = parseFloat(this.discountForm.amount) || 0
      const discount = parseFloat(this.discountForm.discount) || 0
      return Math.max(0, amount - discount)
    },

    // Apply discount to invoice
    async applyDiscount(bvModalEvt) {
      bvModalEvt.preventDefault()

      // Validate discount
      const discount = parseFloat(this.discountForm.discount)
      const amount = parseFloat(this.discountForm.amount)

      if (isNaN(discount) || discount < 0) {
        this.discountError = this.$t('common.invalid-discount')
        return
      }

      if (discount > amount) {
        this.discountError = this.$t('common.discount-exceeds-amount')
        return
      }

      try {
        this.loading = true

        const payload = {
          discount,
        }

        await this.$http.put(`/invoices/${this.discountForm.id}`, payload)

        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.success'),
            variant: 'success',
            text: this.$t('common.discount-applied-successfully'),
          },
        })

        this.$bvModal.hide('modal-apply-discount-refund')

        // Refresh data
        this.$emit('onChangeStatus')
      } catch (error) {
        console.error('Error applying discount:', error)

        // Handle validation errors (400)
        if (error.response && error.response.status === 400) {
          const errors = error.response.data

          // Check if discount field has errors
          if (errors.discount && Array.isArray(errors.discount)) {
            this.discountError = errors.discount[0]
            this.$toast({
              component: ToastificationContent,
              position: 'top-right',
              props: {
                title: this.$t('common.error'),
                variant: 'danger',
                text: errors.discount[0],
              },
            })
          } else {
            // Generic validation error
            const firstError = Object.values(errors)[0]
            const errorMessage = Array.isArray(firstError) ? firstError[0] : firstError
            this.discountError = errorMessage
            this.$toast({
              component: ToastificationContent,
              position: 'top-right',
              props: {
                title: this.$t('common.error'),
                variant: 'danger',
                text: errorMessage,
              },
            })
          }
        } else {
          // Generic error
          this.$toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: this.$t('common.error'),
              variant: 'danger',
              text: this.$t('common.discount-apply-error'),
            },
          })
        }
      } finally {
        this.loading = false
      }
    },

    showRefundInvoice(item) {
      this.invoice = item
      this.currentInvoice = item
      this.$bvModal.show('modal-confirm-print-refund')
    },

    generatePdf() {
      const content = this.$refs.printContent
      if (!content) return

      const invoiceElement = content.querySelector('#RefundInvoice')
      const htmlContent = invoiceElement ? invoiceElement.innerHTML : content.innerHTML

      const styles = Array.from(document.styleSheets)
        .map(styleSheet => {
          try {
            return Array.from(styleSheet.cssRules)
              .map(rule => rule.cssText)
              .join('\n')
          } catch (e) {
            return ''
          }
        })
        .join('\n')

      const fullHtmlContent = `
<!DOCTYPE html>
<html lang="ar" dir="rtl">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${this.$t('accounting.refund-invoice')}</title>
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    body {
      font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
      padding: 12px;
      color: #111827;
      line-height: 1.4;
      direction: rtl;
    }
    #RefundInvoice {
      display: block !important;
      visibility: visible !important;
    }
    .print-content {
      display: block !important;
    }
    .rtl {
      direction: rtl;
    }
    .ltr {
      direction: ltr;
    }
    @media print {
      body {
        padding: 10mm;
      }
      .print-content {
        display: block !important;
      }
      button {
        display: none !important;
      }
      * {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
      }
    }
    ${styles}
  </style>
</head>
<body>
  <div id="RefundInvoice">${htmlContent}</div>
  <script>
    window.onload = function() {
      window.print();
    };
  <\/script>
</body>
</html>
      `

      const printWindow = window.open('', '_blank')
      if (!printWindow) {
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.error'),
            variant: 'danger',
            text: this.$t('common.popup-blocked') || 'Please allow popups for this site',
          },
        })
        return
      }

      printWindow.document.open()
      printWindow.document.write(fullHtmlContent)
      printWindow.document.close()

      setTimeout(() => {
        printWindow.focus()
      }, 100)
    },

    async printInvoice() {
      await this.$nextTick()
      this.generatePdf()

      try {
        await this.$http.put(`/invoices/${this.currentInvoice.id}/changeStatus`, {
          status: 'printed',
        })

        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.success'),
            variant: 'success',
            text: this.$t('common.invoice-printed-successfully'),
          },
        })

        this.$emit('onChangeStatus')
      } catch (error) {
        console.error('Error updating invoice status:', error)
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.error'),
            variant: 'danger',
            text: this.$t('common.error-updating-status'),
          },
        })
      }
    },

    clearSelection() {
      this.selectedInvoices = []
    },

    selectAll() {
      const invoicesData = getNestedProperty(this.invoices, 'data', [])
      this.selectedInvoices = [...invoicesData]
    },

    toggleSelectAll(checked) {
      if (checked) {
        this.selectAll()
      } else {
        this.clearSelection()
      }
    },

    showBulkUpdateModal() {
      if (this.selectedInvoices.length === 0) {
        this.$toast.error(this.$t('common.select-invoices-first'))
        return
      }
      this.bulkUpdateForm.status = null
      this.$bvModal.show('modal-bulk-update-status-refund')
    },

    async bulkUpdateStatus() {
      if (!this.bulkUpdateForm.status) {
        this.$toast.error(this.$t('common.select-status-first'))
        return
      }

      try {
        this.loading = true

        // Prepare the payload
        const payload = {
          invoices: this.selectedInvoices.map(invoice => ({
            id: invoice.id,
            status: this.bulkUpdateForm.status,
          })),
        }

        console.log('Bulk update payload:', payload)

        // Call the bulk update API
        await this.$http.post('/invoices/bulkUpdateStatus', payload)

        this.$toast.success(this.$t('common.bulk-update-success'))

        // Clear selection and refresh data
        this.clearSelection()
        this.$bvModal.hide('modal-bulk-update-status-refund')

        // Emit event to refresh parent data
        this.$emit('onChangeStatus')
      } catch (error) {
        console.error('Error bulk updating invoices:', error)
        this.$toast.error(this.$t('common.bulk-update-error'))
      } finally {
        this.loading = false
      }
    },

    onSelectionChange() {
      // Handle selection change if needed
    },
  },
}
</script>

<style scoped>
.hidden {
  display: none !important;
}

.gap-2 {
  gap: 0.5rem;
}

/* Service Items Container Styling */
.test-items-container {
  min-width: 250px;
  background-color: #f8f9fa;
  border-radius: 6px;
  padding: 8px;
  border: 1px solid #e9ecef;
}

.test-item-row {
  padding: 6px 0;
}

.test-item-row.border-bottom {
  border-bottom: 1px solid #dee2e6 !important;
}

.test-item-name {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
}

.test-item-amount {
  margin-left: 12px;
  white-space: nowrap;
}

/* Modal test items styling */
.test-item-modal-row {
  padding: 8px 0;
}

.test-item-modal-row.border-bottom {
  border-bottom: 1px solid #e9ecef !important;
}

/* Refund description styling */
.refund-description {
  background-color: #fff3cd;
  padding: 10px;
  border-radius: 6px;
  border-left: 3px solid #ffc107;
  max-width: 500px;
}

.refund-description .text-dark {
  color: #856404 !important;
  line-height: 1.4;
}
</style>
